import { useState } from 'react';
import { ImagePreviewModal } from './ImagePreviewModal';

/**
 * ClickableProductImage - Standardized 60x60 product thumbnail with hover zoom
 * and built-in or parent-controlled fullscreen preview modal.
 *
 * @param {Object} props
 * @param {string} props.imageUrl - URL of the product image
 * @param {string} props.productName - Name of the product
 * @param {string} [props.className] - Additional classes for container
 * @param {() => void} [props.onClick] - Optional custom click handler (overrides built-in modal)
 */
export function ClickableProductImage({
  imageUrl,
  productName,
  className = '',
  onClick,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const hasValidImage = Boolean(imageUrl && !imageError);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!hasValidImage) return;

    if (onClick) {
      onClick();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (!hasValidImage) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <>
      <div
        role={hasValidImage ? 'button' : undefined}
        tabIndex={hasValidImage ? 0 : undefined}
        aria-label={
          hasValidImage
            ? `Preview ${productName || 'product'} image`
            : undefined
        }
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`group relative w-[60px] h-[60px] min-w-[60px] min-h-[60px] rounded-[10px] bg-white border border-[#EBF4EA] overflow-hidden shrink-0 flex items-center justify-center select-none ${
          hasValidImage
            ? 'cursor-pointer hover:border-[#50AB3D]/60 hover:shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#50AB3D]/40'
            : 'bg-[#F9FAFB] cursor-default'
        } ${className}`.trim()}
      >
        {hasValidImage ? (
          <img
            src={imageUrl}
            alt={productName || 'Product'}
            onError={() => setImageError(true)}
            className="w-full h-full object-contain p-1 transition-transform duration-200 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#868686] text-xs">
            <svg
              className="w-5 h-5 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Built-in modal when no external onClick is supplied */}
      {!onClick && hasValidImage && (
        <ImagePreviewModal
          isOpen={isModalOpen}
          imageUrl={imageUrl}
          title={productName}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

export default ClickableProductImage;
